import { CreateVisitType } from '@features/visits/entity';
import { useCreateVisitMutation } from '@features/visits/queries';
import { useForm } from 'react-hook-form';
import { useIntlTranslations } from '@libs/intl/intl.hooks';
import { useAddSnackbarMessage } from '@shared/useAddSnackBar';
import { useServicesQuery } from '@features/service/queries';
import { getServiceDurationMinutes } from '@features/service/entity';
import dayjs from 'dayjs';
import { getDefaultPhonePrefix } from '@shared/components/phone-prefix-autocomplete';
import { queryClient } from '@shared/queries';
import { useParams } from 'react-router';

export const useVisitFormDialog = () => {
  const methods = useForm<CreateVisitType>({
    defaultValues: { depositRequired: false, phonePrefix: getDefaultPhonePrefix() },
  });
  const { visitId } = useParams();
  const { t } = useIntlTranslations();
  const addSnackbarMessage = useAddSnackbarMessage();
  const { data: services } = useServicesQuery();

  const { mutate: mutateCreateVisit } = useCreateVisitMutation({
    onSuccess: () => {
      methods.resetField('clientId');
      methods.resetField('serviceIds');
      queryClient.invalidateQueries({ queryKey: ['visitPaymentQr', visitId] });
    },
  });

  const createVisitMutation = (data: CreateVisitType): false | void => {
    if (!data.date || isNaN(new Date(data.date).getTime())) {
      methods.setError('date', { message: t('addVisitForm.chooseDateTime') });
      addSnackbarMessage({
        text: t('addVisitForm.chooseDateTime'),
        type: 'error',
      });
      return false;
    }

    let resolvedData = data;
    if (data.manualDateTimeOverride && data.serviceIds && data.date && data.dateTo) {
      const service = services?.find(s => s.id === data.serviceIds);
      if (service) {
        const actualMinutes = dayjs(data.dateTo).diff(dayjs(data.date), 'minute');
        if (actualMinutes === getServiceDurationMinutes(service)) {
          resolvedData = { ...data, manualDateTimeOverride: false };
        }
      }
    }

    mutateCreateVisit(resolvedData);
  };

  return { methods, createVisitMutation };
};
